import React, { useRef, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperType } from 'swiper';
import SwiperCore, { Navigation } from 'swiper';
import 'swiper/swiper-bundle.min.css';
import { useTranslation, Trans } from 'react-i18next';
import AppButton from 'components/AppButton';
import SectionHeading from 'components/SectionHeading';
import SectionSubtitle from 'components/SectionSubtitle';
import titleUnderline from '../OnlineRepairs/0707.png';
import { SATISFIED_CUSTOMERS_TESTIMONIALS } from './testimonialsData';
import styles from './SatisfiedCustomersSlider.module.css';

SwiperCore.use([Navigation]);

const TRUSTPILOT_URL = 'https://de.trustpilot.com/review/repair-rebels.com';

function ChevronLeft() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M15 18l-6-6 6-6" />
    </svg>
  );
}

function ChevronRight() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 18l6-6-6-6" />
    </svg>
  );
}

function TrustpilotStars() {
  return (
    <span className={styles.stars} aria-hidden>
      {Array.from({ length: 5 }, (_, i) => (
        <svg key={i} className={styles.star} viewBox="0 0 24 24">
          <rect width="24" height="24" fill="#00B67A" />
          <g transform="translate(12 12) scale(0.72) translate(-12 -12)">
            <path
              fill="#ffffff"
              d="M12,17.964l5.214-1.321l2.179,6.714L12,17.964z M24,9.286h-9.179L12,0.643L9.179,9.286
                 H0l7.429,5.357l-2.821,8.643l7.429-5.357l4.571-3.286L24,9.286z"
            />
          </g>
        </svg>
      ))}
    </span>
  );
}

export default function SatisfiedCustomersSlider() {
  const { t } = useTranslation();
  const swiperRef = useRef<SwiperType | null>(null);
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);
  const canLoop = SATISFIED_CUSTOMERS_TESTIMONIALS.length > 1;

  const updateNavState = (swiper: SwiperType) => {
    setIsBeginning(swiper.isBeginning);
    setIsEnd(swiper.isEnd);
  };

  return (
    <section className={styles.section} aria-labelledby="satisfied-customers-heading">
      <div className={styles.container}>
        <header className={styles.header}>
          <div className={styles.titleWrapper}>
            <SectionHeading id="satisfied-customers-heading" size="large" className={styles.title}>
              {t('Satisfied customers title')}
            </SectionHeading>
            <img src={titleUnderline} alt="" className={styles.titleUnderline} aria-hidden />
          </div>
          <SectionSubtitle size="medium" className={styles.subtitle}>{t('Satisfied customers subtitle')}</SectionSubtitle>
        </header>

        <div className={styles.sliderWrapper}>
          <Swiper
            onSwiper={(swiper) => {
              swiperRef.current = swiper;
              updateNavState(swiper);
            }}
            onSlideChange={(swiper) => updateNavState(swiper)}
            slidesPerView={1}
            slidesPerGroup={1}
            autoHeight
            loop={canLoop}
            className={styles.slider}
          >
            {SATISFIED_CUSTOMERS_TESTIMONIALS.map((item, index) => (
              <SwiperSlide key={index} className={styles.slide}>
                <p className={styles.quote}>
                  <span className={`${styles.quoteMark} ${styles.quoteMarkOpen}`} aria-hidden>
                    &ldquo;
                  </span>
                  {t(item.commentKey)}
                  <span className={`${styles.quoteMark} ${styles.quoteMarkClose}`} aria-hidden>
                    &rdquo;
                  </span>
                </p>
                <div className={styles.nameBlock}>
                  <span className={styles.firstName}>{item.firstName}</span>
                  <span className={styles.lastName}>{item.lastName}</span>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>

        <div className={styles.navWrapper}>
          <AppButton
            color="secondary"
            variant="contained"
            type="button"
            className={styles.navButton}
            onClick={() => swiperRef.current?.slidePrev()}
            disabled={!canLoop && isBeginning}
            aria-label={t('Previous slide')}
          >
            <ChevronLeft />
          </AppButton>
          <AppButton
            color="secondary"
            variant="contained"
            type="button"
            className={styles.navButton}
            onClick={() => swiperRef.current?.slideNext()}
            disabled={!canLoop && isEnd}
            aria-label={t('Next slide')}
          >
            <ChevronRight />
          </AppButton>
        </div>

        <a
          className={styles.trustpilot}
          href={TRUSTPILOT_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          <span className={styles.trustpilotLabel}>{t('Excellent')}</span>
          <TrustpilotStars />
          <span className={styles.trustpilotWordmark}>Trustpilot</span>
        </a>

        <p className={styles.scriptQuote} tabIndex={0}>
          <Trans
            i18nKey="Satisfied customers motto"
            components={{ 1: <span className={styles.scriptHighlight} /> }}
          />
        </p>
      </div>
    </section>
  );
}
